import React from "react";
import {useTranslation} from "react-i18next";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {combineSnapshotPublicationDisplay, publicationDateList} from "../../utils/reportingSnapshotAvailability.js";

const statusPanelClass = "app-surface col-span-12 flex w-full flex-col gap-3 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100";
const statusContentClass = "flex w-full flex-wrap items-center gap-x-1 gap-y-1";
const statusSectionClass = "grid w-full grid-cols-1 gap-1 sm:grid-cols-[minmax(12rem,0.32fr)_minmax(0,1fr)]";

function StatusPanel({children}) {
    return <div role="status" className={statusPanelClass}>{children}</div>;
}

function StatusSection({inline, title, children}) {
    return inline
        ? <section className={statusSectionClass}>
            <h3 className="font-semibold">{title}</h3>
            <div className={statusContentClass}>{children}</div>
        </section>
        : <section className={statusContentClass}>
            {title && <span className="font-semibold">{title}</span>}
            {children}
        </section>;
}

function PublicationNotice({label, snapshots, now, t, locale, inline = false}) {
    const display = combineSnapshotPublicationDisplay(snapshots, {now});
    if (display.confirmedDataAbsentDays.length === 0) return null;

    const missingText = t("reporting_lab.snapshot.missing_dates", {
        defaultValue: "Přehled nezahrnuje {{dates}}.",
        dates: publicationDateList(display.confirmedDataAbsentDays, locale),
    });
    const title = t("reporting_lab.snapshot.missing_heading", {defaultValue: locale.startsWith("en") ? "The selected period has incomplete data." : "Ve zvoleném období nemáme kompletní data."});

    const content = <>
        <span className="font-semibold">{title}</span>
        <span>{missingText}</span>
        <ReportingInfoTooltip>
            <p>{t("reporting_lab.snapshot.confirmed_absence_help", {defaultValue: "Za uvedené dny nemáme žádná data. Ostatní neúplné nebo odhadované hodnoty neposuzujeme jako den bez dat."})}</p>
            <p>{missingText}</p>
        </ReportingInfoTooltip>
    </>;
    const section = <StatusSection
        inline={inline}
        title={inline ? (label ?? t("reporting_lab.snapshot.publication_group_heading", {defaultValue: locale.startsWith("en") ? "Report availability" : "Dostupnost přehledu"})) : label ? `${label}:` : null}
    >{content}</StatusSection>;

    return inline ? section : <StatusPanel>{section}</StatusPanel>;
}

export default function ReportingSnapshotStatus({snapshots, comparisonSnapshot, now, label = null}) {
    const {t, i18n} = useTranslation();
    const locale = i18n?.language ?? "cs";
    const currentSnapshots = (Array.isArray(snapshots) ? snapshots : [snapshots]).filter(Boolean);
    const comparisonSnapshots = (Array.isArray(comparisonSnapshot) ? comparisonSnapshot : [comparisonSnapshot]).filter(Boolean);

    return <>
        <PublicationNotice label={label} snapshots={currentSnapshots} now={now} t={t} locale={locale}/>
        <PublicationNotice
            label={t("common.previous-period-value", {defaultValue: "Předchozí období"})}
            snapshots={comparisonSnapshots}
            now={now}
            t={t}
            locale={locale}
        />
    </>;
}
